import React, { useState, useEffect } from 'react'
import { Download, FileText, FileCode, Presentation, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ExportOptions, ExportResult } from './types'

export interface ExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onExport: (options: ExportOptions) => Promise<ExportResult>
  presentationTitle: string
  estimatedSize?: {
    pdf: string
    pptx: string
    html: string
  }
}

const formatOptions = [
  {
    value: 'pdf' as const,
    label: 'PDF',
    description: 'Best for sharing and printing',
    icon: FileText,
  },
  {
    value: 'pptx' as const,
    label: 'PowerPoint',
    description: 'Editable presentation format',
    icon: Presentation,
  },
  {
    value: 'html' as const,
    label: 'HTML',
    description: 'Standalone web page',
    icon: FileCode,
  },
]

const qualityOptions = [
  { value: 'low' as const, label: 'Low (Smallest file)', size: '~1-2 MB' },
  { value: 'medium' as const, label: 'Medium (Balanced)', size: '~3-5 MB' },
  { value: 'high' as const, label: 'High (Best quality)', size: '~5-10 MB' },
]

/**
 * ExportDialog Component
 *
 * Modal dialog for selecting export format and options.
 * Shows format options, quality settings, and estimated file sizes.
 */
export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onOpenChange,
  onExport,
  presentationTitle,
  estimatedSize = { pdf: '~3-5 MB', pptx: '~2-4 MB', html: '~1-2 MB' },
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportOptions['format']>('pdf')
  const [quality, setQuality] = useState<ExportOptions['quality']>('high')
  const [includeNotes, setIncludeNotes] = useState(false)
  const [includeSlideNumbers, setIncludeSlideNumbers] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedFormat('pdf')
      setQuality('high')
      setIncludeNotes(false)
      setIncludeSlideNumbers(true)
      setError(null)
    }
  }, [open])

  const handleExport = async () => {
    setIsExporting(true)
    setError(null)

    try {
      const options: ExportOptions = {
        format: selectedFormat,
        quality,
        includeNotes,
        includeSlideNumbers,
      }

      const result = await onExport(options)

      if (!result.success && result.error) {
        setError(result.error)
      } else {
        // Close dialog on success
        onOpenChange(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setIsExporting(false)
    }
  }

  const getEstimatedFileSize = () => {
    if (selectedFormat === 'pdf') {
      return qualityOptions.find((q) => q.value === quality)?.size || estimatedSize.pdf
    }
    return estimatedSize[selectedFormat]
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Presentation
          </DialogTitle>
          <DialogDescription>
            Choose format and quality options for exporting "{presentationTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Export Format
            </label>
            <div className="grid grid-cols-1 gap-2">
              {formatOptions.map((format) => {
                const Icon = format.icon
                const isSelected = selectedFormat === format.value
                return (
                  <button
                    key={format.value}
                    onClick={() => setSelectedFormat(format.value)}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left',
                      'hover:bg-gray-50 dark:hover:bg-gray-800',
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700'
                    )}
                  >
                    <div
                      className={cn(
                        'mt-0.5',
                        isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span
                          className={cn(
                            'font-medium',
                            isSelected
                              ? 'text-primary-900 dark:text-primary-100'
                              : 'text-gray-900 dark:text-gray-100'
                          )}
                        >
                          {format.label}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        )}
                      </div>
                      <p
                        className={cn(
                          'text-sm mt-0.5',
                          isSelected
                            ? 'text-primary-700 dark:text-primary-300'
                            : 'text-gray-500 dark:text-gray-400'
                        )}
                      >
                        {format.description}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Est. size: {estimatedSize[format.value]}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Quality Options (PDF only) */}
          {selectedFormat === 'pdf' && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Quality
              </label>
              <div className="grid grid-cols-1 gap-2">
                {qualityOptions.map((qOption) => (
                  <button
                    key={qOption.value}
                    onClick={() => setQuality(qOption.value)}
                    className={cn(
                      'flex items-center justify-between p-3 rounded-lg border-2 transition-all',
                      'hover:bg-gray-50 dark:hover:bg-gray-800',
                      quality === qOption.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700'
                    )}
                  >
                    <span
                      className={cn(
                        'font-medium text-sm',
                        quality === qOption.value
                          ? 'text-primary-900 dark:text-primary-100'
                          : 'text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {qOption.label}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{qOption.size}</span>
                      {quality === qOption.value && (
                        <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Additional Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Additional Options
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={includeSlideNumbers}
                  onChange={(e) => setIncludeSlideNumbers(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Include slide numbers
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Add page numbers to exported file
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={includeNotes}
                  onChange={(e) => setIncludeNotes(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Include presenter notes
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Add speaker notes if available (PPTX only)
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Estimated File Size */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Estimated file size:
              </span>
              <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                {getEstimatedFileSize()}
              </span>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : `Export as ${selectedFormat.toUpperCase()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return null
}

export default ExportDialog
